import {
  CALLOUT_COLLAPSED_ATTR,
  CALLOUT_TITLE_ATTR,
  CALLOUT_TYPE_ATTR,
  type CalloutType,
  calloutTokenizer,
  readCalloutTokenMetadata,
  resolveCalloutType,
  serializeCalloutHeader,
} from '@bangle.io/markdown-syntax';
import { type CollectionType, collection, keybinding } from './common';
import type { Attrs, Command, NodeSpec, NodeType, Schema } from './pm';
import {
  findWrapping,
  InputRule,
  inputRules,
  lift,
  type PMNode,
  wrapIn,
} from './pm';
import {
  defaultGetParagraphNodeType,
  findParentNodeOfType,
  getNodeType,
  insertEmptyParagraphAboveNode,
  insertEmptyParagraphBelowNode,
  type KeyCode,
  moveNode,
  type PluginContext,
  setParentNodeMarkup,
} from './pm-utils';

export type { CalloutType } from '@bangle.io/markdown-syntax';

/** The attributes of a callout node. */
export type CalloutAttrs = {
  /** Canonical type; MkDocs aliases are normalized on parse. */
  type: CalloutType;
  /**
   * `null` when the header has no title (the type's default title is shown);
   * `''` is an explicit empty title (no title shown).
   */
  title: string | null;
  /** `true` for the collapsible `???` form, `false` for `!!!`. */
  collapsed: boolean;
};

export type CalloutConfig = {
  name?: string;
  /** Typing `!!! note ` / `??? tip ` at the start of a paragraph wraps it. */
  markdownShortcut?: boolean;
  /**
   * Titles shown when a callout has no explicit title. Banger-editor is
   * translation-agnostic; callers pass localized text. Missing entries fall
   * back to the capitalized type name.
   */
  defaultTitles?: Partial<Record<CalloutType, string>>;
  getParagraphNodeType?: (arg: Schema) => NodeType;
  // keys
  keyMoveUp?: KeyCode;
  keyMoveDown?: KeyCode;
  keyInsertEmptyParaAbove?: KeyCode;
  keyInsertEmptyParaBelow?: KeyCode;
};

type RequiredConfig = Required<CalloutConfig>;

const DEFAULT_CONFIG: RequiredConfig = {
  name: 'callout',
  markdownShortcut: true,
  defaultTitles: {},
  getParagraphNodeType: defaultGetParagraphNodeType,
  keyMoveUp: 'Alt-ArrowUp',
  keyMoveDown: 'Alt-ArrowDown',
  keyInsertEmptyParaAbove: 'Mod-Shift-Enter',
  keyInsertEmptyParaBelow: 'Mod-Enter',
};

const DEFAULT_ATTRS: CalloutAttrs = {
  type: 'note',
  title: null,
  collapsed: false,
};

const SHORTCUT_RE = /^(!!!|\?\?\?)[ \t]+([A-Za-z][A-Za-z0-9_-]*)[ \t]$/u;

/**
 * MkDocs-style callouts (`!!! note "Title"` / `??? warning`). A dedicated
 * block node rather than an overloaded blockquote, so plain `>` quotes keep
 * their exact Markdown meaning and callouts get their own typed attrs.
 */
export function setupCallout(userConfig?: CalloutConfig) {
  const config: RequiredConfig = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };
  const { name } = config;

  const nodes = {
    [name]: {
      group: 'block',
      content: 'block+',
      defining: true,
      attrs: {
        type: { default: DEFAULT_ATTRS.type },
        title: { default: DEFAULT_ATTRS.title },
        collapsed: { default: DEFAULT_ATTRS.collapsed },
      },
      parseDOM: [
        {
          tag: `div[${CALLOUT_TYPE_ATTR}]`,
          contentElement: '.bangle-callout-body',
          getAttrs: (dom) => {
            if (typeof dom === 'string') {
              return false;
            }
            return {
              type:
                resolveCalloutType(dom.getAttribute(CALLOUT_TYPE_ATTR) ?? '') ??
                DEFAULT_ATTRS.type,
              title: dom.getAttribute(CALLOUT_TITLE_ATTR),
              collapsed: dom.getAttribute(CALLOUT_COLLAPSED_ATTR) === 'true',
            };
          },
        },
      ],
      toDOM: (node) => {
        const attrs = readCalloutAttrs(node.attrs);
        const domAttrs: Record<string, string> = {
          class: 'bangle-callout',
          [CALLOUT_TYPE_ATTR]: attrs.type,
          [CALLOUT_COLLAPSED_ATTR]: String(attrs.collapsed),
        };
        if (attrs.title !== null) {
          domAttrs[CALLOUT_TITLE_ATTR] = attrs.title;
        }
        return [
          'div',
          domAttrs,
          [
            'div',
            { class: 'bangle-callout-title', contenteditable: 'false' },
            attrs.title ?? defaultTitle(config, attrs.type),
          ],
          ['div', { class: 'bangle-callout-body' }, 0],
        ];
      },
    } satisfies NodeSpec,
  };

  const plugin = {
    inputRules: pluginInputRules(config),
    keybindings: pluginKeybindings(config),
  };

  const command = {
    wrapCallout: wrapCallout(config),
    toggleCallout: toggleCallout(config),
    setCalloutAttrs: setCalloutAttrs(config),
    moveCalloutUp: moveCalloutUp(config),
    moveCalloutDown: moveCalloutDown(config),
    insertEmptyParaAbove: insertEmptyParaAboveCallout(config),
    insertEmptyParaBelow: insertEmptyParaBelowCallout(config),
  };

  return collection({
    id: 'callout',
    nodes,
    plugin,
    command,
    query: {
      isCalloutActive: isCalloutActive(config),
    },
    markdown: markdown(config),
  });
}

/** Narrow raw node attrs to a well-formed {@link CalloutAttrs}. */
export function readCalloutAttrs(attrs: Attrs): CalloutAttrs {
  const type =
    typeof attrs.type === 'string' ? resolveCalloutType(attrs.type) : null;
  return {
    type: type ?? DEFAULT_ATTRS.type,
    title: typeof attrs.title === 'string' ? attrs.title : null,
    collapsed: attrs.collapsed === true,
  };
}

function defaultTitle(config: RequiredConfig, type: CalloutType): string {
  return (
    config.defaultTitles[type] ??
    `${type.charAt(0).toUpperCase()}${type.slice(1)}`
  );
}

// PLUGINS
function pluginInputRules(config: RequiredConfig) {
  return ({ schema }: PluginContext) => {
    if (!config.markdownShortcut) {
      return null;
    }
    const nodeType = getNodeType(schema, config.name);
    return inputRules({
      rules: [
        new InputRule(SHORTCUT_RE, (state, match, start, end) => {
          const [, marker, word] = match;
          const type = word ? resolveCalloutType(word) : null;
          if (!type) {
            return null;
          }
          const attrs: CalloutAttrs = {
            type,
            title: null,
            collapsed: marker === '???',
          };
          const tr = state.tr.delete(start, end);
          const range = tr.doc.resolve(start).blockRange();
          const wrapping = range && findWrapping(range, nodeType, attrs);
          if (!range || !wrapping) {
            return null;
          }
          return tr.wrap(range, wrapping);
        }),
      ],
    });
  };
}

function pluginKeybindings(config: RequiredConfig) {
  return () => {
    return keybinding(
      [
        [config.keyMoveUp, moveCalloutUp(config)],
        [config.keyMoveDown, moveCalloutDown(config)],
        [config.keyInsertEmptyParaAbove, insertEmptyParaAboveCallout(config)],
        [config.keyInsertEmptyParaBelow, insertEmptyParaBelowCallout(config)],
      ],
      'callout',
    );
  };
}

// COMMANDS
/** Wrap the selected blocks in a callout. */
function wrapCallout(config: RequiredConfig) {
  return (attrs?: Partial<CalloutAttrs>): Command => {
    return (state, dispatch) => {
      const type = getNodeType(state.schema, config.name);
      return wrapIn(type, { ...DEFAULT_ATTRS, ...attrs })(state, dispatch);
    };
  };
}

/** Unwrap when inside a callout, otherwise wrap the selection in a note. */
function toggleCallout(config: RequiredConfig): Command {
  return (state, dispatch) => {
    if (isCalloutActive(config)(state)) {
      return lift(state, dispatch);
    }
    return wrapCallout(config)()(state, dispatch);
  };
}

/** Update attrs (type, title, collapsed) of the callout around the selection. */
function setCalloutAttrs(config: RequiredConfig) {
  return (attrs: Partial<CalloutAttrs>): Command => {
    return (state, dispatch) => {
      const type = getNodeType(state.schema, config.name);
      if (!findParentNodeOfType(type)(state.selection)) {
        return false;
      }
      dispatch?.(setParentNodeMarkup(type, null, attrs)(state.tr));
      return true;
    };
  };
}

function moveCalloutUp(config: RequiredConfig): Command {
  return (state, dispatch) =>
    moveNode(getNodeType(state.schema, config.name), 'UP')(state, dispatch);
}

function moveCalloutDown(config: RequiredConfig): Command {
  return (state, dispatch) =>
    moveNode(getNodeType(state.schema, config.name), 'DOWN')(state, dispatch);
}

function insertEmptyParaAboveCallout(config: RequiredConfig): Command {
  return (state, dispatch) =>
    insertEmptyParagraphAboveNode(
      getNodeType(state.schema, config.name),
      config.getParagraphNodeType,
    )(state, dispatch);
}

function insertEmptyParaBelowCallout(config: RequiredConfig): Command {
  return (state, dispatch) =>
    insertEmptyParagraphBelowNode(
      getNodeType(state.schema, config.name),
      config.getParagraphNodeType,
    )(state, dispatch);
}

// QUERIES
function isCalloutActive(config: RequiredConfig): Command {
  return (state) => {
    const type = getNodeType(state.schema, config.name);
    return Boolean(findParentNodeOfType(type)(state.selection));
  };
}

// MARKDOWN
/** A body that is a single empty textblock carries no source to write. */
function hasEmptyBody(node: PMNode): boolean {
  const first = node.firstChild;
  return (
    node.childCount === 1 &&
    first !== null &&
    first.isTextblock &&
    first.content.size === 0
  );
}

function markdown(config: RequiredConfig): CollectionType['markdown'] {
  const { name } = config;
  return {
    nodes: {
      [name]: {
        toMarkdown: (state, node) => {
          state.write(serializeCalloutHeader(readCalloutAttrs(node.attrs)));
          if (hasEmptyBody(node)) {
            state.closeBlock(node);
            return;
          }
          state.ensureNewLine();
          // The body is indented four spaces, as MkDocs requires. Blank
          // lines between body blocks are trimmed by the serializer.
          state.wrapBlock('    ', null, node, () => state.renderContent(node));
        },
        parseMarkdown: {
          callout: {
            block: name,
            getAttrs: (token) => ({ ...readCalloutTokenMetadata(token) }),
          },
        },
      },
    },
    tokenizerPlugins: [calloutTokenizer],
  };
}
